/**
 * product-card-interactions.js
 * Shared product card UI: color swatches (preserve Color key across non-Color option clicks via variant title leading segment), disable non-Color pills when incompatible with active Color (data-pci-option-position vs variant titles), other variant rows, price rows, add-to-cart data-variant-id, optional
 * mobile primary image layer (product-lifestyle-card), lifestyle scene Swiper. Pass config via data-pci-* on a root or init().
 *
 * Variant discount map (data-pci-variant-discount-labels JSON + optional data-pci-variant-discount-overrides lines):
 * - String value: badge + data-discount both that text
 * - null: hide badge, data-discount ""
 * - Object { badge, discount }: discount empty/missing => data-discount ""; badge drives visible strip when non-empty
 *
 * Line overrides (merged on top of JSON): id:text | id:null | id:@marketingOnly
 *
 * Variant prices (data-pci-variant-prices JSON + optional data-pci-variant-price-overrides lines):
 * - Each variant id maps to { price, compare_price }. Empty compare_price hides strike-through for that variant.
 * - Line format: variantId:{"price":"$199.95","compare_price":""} (JSON after first colon). Lines win over JSON for same id.
 * - .product_info data-price / data-com-price follow the active swatch; data-product-default-* hold block defaults.
 * Hidden variants: data-pci-hidden-variant-ids (comma-separated) adds class pci-variant--config-hidden; excluded from autoselect / preferred color / size-validity sets.
 * @see snippets/product-lifestyle-card.liquid
 */
(function () {
  'use strict';

  if (globalThis.__productCardInteractionsBundleLoaded) {
    return;
  }
  globalThis.__productCardInteractionsBundleLoaded = true;

  const EVENT_CONTENT_UPDATED = 'product-card-interactions:content-updated';
  /** Fallback when data-plc-scene-desktop-min is missing — matches snippet default breakpoint_mobile 900px */
  const PLC_SCENE_FALLBACK_DESKTOP_MIN_PX = 901;

  /** @param {JQuery} $cardRoot */
  function getPciGa4PageName ($cardRoot) {
    if (!$cardRoot || !$cardRoot.length) return '';
    return $cardRoot.attr('data-ga4-page-name') || '';
  }

  /** @type {Record<string, number>} */
  const ga4LastPush = {};

  /** @param {string} contentType @param {string} buttonName @param {string} pageName */
  function pushGa4SelectContent (contentType, buttonName, pageName) {
    if (!pageName) return;
    const now = Date.now();
    const last = ga4LastPush[contentType] || 0;
    if (now - last < 500) return;
    ga4LastPush[contentType] = now;
    const contentName = contentType + '_' + pageName;
    window.dataLayer.push({ event_parameters: null });
    window.dataLayer.push({
      event: 'ga4Event',
      event_name: 'select_content',
      event_parameters: {
        content_type: contentType,
        content_name: contentName,
        button_name: buttonName,
      },
    });
  }

  function getPlcSceneDesktopMinPx (swiperRoot) {
    const card = swiperRoot.closest('.product-lifestyle-card');
    const n = card && card.getAttribute('data-plc-scene-desktop-min');
    if (n == null || String(n).trim() === '') {
      return PLC_SCENE_FALLBACK_DESKTOP_MIN_PX;
    }
    const parsed = parseInt(String(n).trim(), 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
    return PLC_SCENE_FALLBACK_DESKTOP_MIN_PX;
  }

  function isPlcSceneLayoutDesktop (swiperRoot) {
    const minPx = getPlcSceneDesktopMinPx(swiperRoot);
    return window.matchMedia('(min-width: ' + minPx + 'px)').matches;
  }

  const defaultSelectors = {
    mobileConfigLayer: '.product-lifestyle-card__mobile-primary-config',
    mobileConfigImg: '.product-lifestyle-card__mobile-config-img',
    desktopVariantMedia: '.product-lifestyle-card__variant-media--desktop',
    miniProductImgLink: '.mini-product-img-link',
  };

  function mergeDiscountMaps (fromJson, fromLines) {
    return { ...(fromJson || {}), ...(fromLines || {}) };
  }

  function mergePriceMaps (fromJson, fromLines) {
    return { ...(fromJson || {}), ...(fromLines || {}) };
  }

  function mergeOptions (base) {
    const o = base || {};
    let src = o.variantDiscountLabelsParsed;
    if (
      src == null &&
      o.variantDiscountLabels &&
      typeof o.variantDiscountLabels === 'object' &&
      !Array.isArray(o.variantDiscountLabels)
    ) {
      src = o.variantDiscountLabels;
    }
    const merged = mergeDiscountMaps(src, {});
    let priceSrc = o.variantPriceLabelsParsed;
    if (
      priceSrc == null &&
      o.variantPriceLabels &&
      typeof o.variantPriceLabels === 'object' &&
      !Array.isArray(o.variantPriceLabels)
    ) {
      priceSrc = o.variantPriceLabels;
    }
    const mergedPrices = mergePriceMaps(priceSrc || {}, {});
    return {
      defaultVariantIds: o.defaultVariantIds != null ? o.defaultVariantIds : [],
      hiddenVariantIds: o.hiddenVariantIds != null ? o.hiddenVariantIds : [],
      swatchAutoclickBlocklist: o.swatchAutoclickBlocklist || { dataContains: [], dataIds: [] },
      variantDiscountLabels: merged,
      variantPriceLabels: mergedPrices,
      selectors: { ...defaultSelectors, ...(o.selectors || {}) },
    };
  }

  function parseIdsAttr (str) {
    if (str == null || String(str).trim() === '') {
      return [];
    }
    return String(str)
      .split(',')
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean);
  }

  function parseVariantDiscountMapJSON (str) {
    if (str == null || String(str).trim() === '') {
      return {};
    }
    try {
      const parsed = JSON.parse(String(str));
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return {};
      }
      const out = {};
      Object.keys(parsed).forEach(function (k) {
        if (Object.prototype.hasOwnProperty.call(parsed, k)) {
          out[String(k)] = parsed[k];
        }
      });
      return out;
    } catch (err) {
      return {};
    }
  }

  function parseLineDiscountOverrides (str) {
    const out = {};
    if (str == null || !String(str).trim()) {
      return out;
    }
    const lines = String(str)
      .split(/\r?\n/)
      .map(function (l) {
        return l.trim();
      })
      .filter(Boolean);
    for (let li = 0; li < lines.length; li += 1) {
      const line = lines[li];
      const idx = line.indexOf(':');
      if (idx < 0) continue;
      const id = line.slice(0, idx).trim();
      let rest = line.slice(idx + 1).trim();
      if (!id) continue;
      if (/^null$/i.test(rest)) {
        out[id] = null;
      } else if (rest.charAt(0) === '@') {
        const badge = rest.slice(1).trim();
        out[id] = badge ? { badge: badge, discount: '' } : null;
      } else if (rest === '') {
        out[id] = null;
      } else {
        out[id] = rest;
      }
    }
    return out;
  }

  /**
   * @param {unknown} raw
   * @returns {{ price: string, compare_price: string } | null}
   */
  function normalizePriceEntry (raw) {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
      return null;
    }
    return {
      price: raw.price != null ? String(raw.price) : '',
      compare_price: raw.compare_price != null ? String(raw.compare_price) : '',
    };
  }

  function parseVariantPricesMapJSON (str) {
    const loose = parseVariantDiscountMapJSON(str);
    const out = {};
    Object.keys(loose).forEach(function (k) {
      const norm = normalizePriceEntry(loose[k]);
      if (norm) {
        out[String(k)] = norm;
      }
    });
    return out;
  }

  function parseLineVariantPriceOverrides (str) {
    const out = {};
    if (str == null || !String(str).trim()) {
      return out;
    }
    const lines = String(str)
      .split(/\r?\n/)
      .map(function (l) {
        return l.trim();
      })
      .filter(Boolean);
    for (let li = 0; li < lines.length; li += 1) {
      const line = lines[li];
      const idx = line.indexOf(':');
      if (idx < 0) continue;
      const id = line.slice(0, idx).trim();
      const rest = line.slice(idx + 1).trim();
      if (!id || !rest) continue;
      try {
        const parsed = JSON.parse(rest);
        const norm = normalizePriceEntry(parsed);
        if (norm) {
          out[id] = norm;
        }
      } catch (err) {
        // ignore invalid line
      }
    }
    return out;
  }

  function readOptionsFromEl (el) {
    if (!el || !el.getAttribute) {
      return mergeOptions(null);
    }
    const fromJson = parseVariantDiscountMapJSON(el.getAttribute('data-pci-variant-discount-labels'));
    const fromLines = parseLineDiscountOverrides(el.getAttribute('data-pci-variant-discount-overrides'));
    const mergedLabels = mergeDiscountMaps(fromJson, fromLines);
    const priceFromJson = parseVariantPricesMapJSON(el.getAttribute('data-pci-variant-prices'));
    const priceFromLines = parseLineVariantPriceOverrides(el.getAttribute('data-pci-variant-price-overrides'));
    const mergedPrices = mergePriceMaps(priceFromJson, priceFromLines);
    return mergeOptions({
      defaultVariantIds: parseIdsAttr(el.getAttribute('data-pci-default-variant-ids')),
      hiddenVariantIds: parseIdsAttr(el.getAttribute('data-pci-hidden-variant-ids')),
      variantDiscountLabelsParsed: mergedLabels,
      variantDiscountLabels: mergedLabels,
      variantPriceLabelsParsed: mergedPrices,
      variantPriceLabels: mergedPrices,
      swatchAutoclickBlocklist: (function () {
        const raw = el.getAttribute('data-pci-swatch-blocklist');
        if (!raw) {
          return { dataContains: [], dataIds: [] };
        }
        try {
          return JSON.parse(raw);
        } catch (e) {
          return { dataContains: [], dataIds: [] };
        }
      })(),
    });
  }

  function getMergedOptionsForRoot ($root, explicit) {
    const el = $root && $root[0] ? $root[0] : null;
    const fromData = el ? readOptionsFromEl(el) : mergeOptions(null);
    const ex = explicit ? mergeOptions(explicit) : mergeOptions(null);
    const overlayLabels =
      explicit && explicit.variantDiscountLabels && typeof explicit.variantDiscountLabels === 'object'
        ? explicit.variantDiscountLabels
        : null;
    const mergedVariantMap = overlayLabels ? { ...fromData.variantDiscountLabels, ...overlayLabels } : fromData.variantDiscountLabels;
    const overlayPriceLabels =
      explicit && explicit.variantPriceLabels && typeof explicit.variantPriceLabels === 'object'
        ? explicit.variantPriceLabels
        : null;
    const mergedPriceMap = overlayPriceLabels
      ? { ...fromData.variantPriceLabels, ...overlayPriceLabels }
      : fromData.variantPriceLabels;
    return {
      defaultVariantIds: (explicit && explicit.defaultVariantIds != null
        ? ex.defaultVariantIds
        : fromData.defaultVariantIds) || [],
      hiddenVariantIds: (explicit && explicit.hiddenVariantIds != null
        ? ex.hiddenVariantIds
        : fromData.hiddenVariantIds) || [],
      swatchAutoclickBlocklist:
        explicit && explicit.swatchAutoclickBlocklist
          ? ex.swatchAutoclickBlocklist
          : fromData.swatchAutoclickBlocklist,
      variantDiscountLabels: mergedVariantMap,
      variantPriceLabels: mergedPriceMap,
      selectors: (explicit && explicit.selectors ? { ...ex.selectors } : fromData.selectors) || defaultSelectors,
    };
  }

  function resolveVariantDiscountState (variantId, map, defaultDiscountAttr) {
    const vid = String(variantId);
    const fallback = defaultDiscountAttr == null ? '' : String(defaultDiscountAttr);
    const fallbackTrimmed = fallback.trim();
    const base = {
      showBadge: fallbackTrimmed !== '',
      badgeText: fallbackTrimmed,
      dataDiscount: fallback,
    };
    if (!map || !Object.prototype.hasOwnProperty.call(map, vid)) {
      return base;
    }
    const raw = map[vid];
    if (raw === null) {
      return { showBadge: false, badgeText: '', dataDiscount: '' };
    }
    if (typeof raw === 'string') {
      const t = raw.trim();
      if (t === '' || /^null$/i.test(t)) {
        return { showBadge: false, badgeText: '', dataDiscount: '' };
      }
      return { showBadge: true, badgeText: t, dataDiscount: t };
    }
    if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
      const badgeRaw = raw.badge != null ? String(raw.badge).trim() : '';
      let discRaw = raw.discount != null ? String(raw.discount).trim() : '';
      if (!badgeRaw && discRaw) {
        return { showBadge: true, badgeText: discRaw, dataDiscount: discRaw };
      }
      if (!badgeRaw && !discRaw) {
        return { showBadge: false, badgeText: '', dataDiscount: '' };
      }
      if (badgeRaw && !discRaw) {
        return { showBadge: true, badgeText: badgeRaw, dataDiscount: '' };
      }
      return { showBadge: true, badgeText: badgeRaw || discRaw, dataDiscount: discRaw };
    }
    const t = String(raw);
    return { showBadge: true, badgeText: t, dataDiscount: t };
  }

  /**
   * @param {jQuery} $buyCardArea - .buy-card wrapping .product-item
   * @param {string|number} variantId
   * @param {ReturnType<getMergedOptionsForRoot>} options
   */
  function applyDiscountUI ($buyCardArea, variantId, options) {
    const $items = $buyCardArea.find('.product-item.product_info');
    if (!$items.length) return;
    $items.each(function () {
      const $info = $(this);
      const $priceLists = $buyCardArea.find('.price_list');
      const $activePriceList = $priceLists.filter('.active').first();
      const ds = $activePriceList.length > 0 && $priceLists.index($activePriceList) > 0 ? '2' : '';
      const defaultDisc = String($info.attr('data-product' + ds + '-default-discount') || '');
      const map = options.variantDiscountLabels || {};
      const st = resolveVariantDiscountState(variantId, map, defaultDisc);
      const $wraps = $info.find('.discount');
      const $scopes = $wraps.length ? $wraps : $buyCardArea.find('.discount');
      $scopes.each(function () {
        const $w = $(this);
        const $span = $w.find('span').first();
        if (st.showBadge && st.badgeText) {
          $w.removeClass('product-lifestyle-card__discount--hidden');
          $w.removeAttr('hidden');
          $span.text(st.badgeText);
        } else {
          $w.addClass('product-lifestyle-card__discount--hidden');
          $w.attr('hidden', 'hidden');
          $span.text('');
        }
      });
      $info.attr('data-discount', st.dataDiscount);
    });
  }

  function syncDiscountFromActiveSwatchInCardRoot ($cardRoot) {
    if (!$cardRoot || !$cardRoot.length) return;
    const opts = getMergedOptionsForRoot($cardRoot, null);
    const $item = $cardRoot.find('.product_item').first();
    const $active = $item.find('.product-color-swatch-wrapper .swatch.active').first();
    if (!$active.length) return;
    const variantId = $active.data('id');
    applyDiscountUI($cardRoot.find('.buy-card'), variantId, opts);
  }

  function resolveVariantPriceState (variantId, map, defaultPrice, defaultCompare) {
    const vid = String(variantId);
    const defP = defaultPrice == null ? '' : String(defaultPrice);
    const defC = defaultCompare == null ? '' : String(defaultCompare);
    if (!map || !Object.prototype.hasOwnProperty.call(map, vid)) {
      return { price: defP, compare_price: defC };
    }
    const norm = normalizePriceEntry(map[vid]);
    if (norm) {
      return norm;
    }
    return { price: defP, compare_price: defC };
  }

  /**
   * @param {jQuery} $buyCardArea
   * @param {string|number} activeVariantId
   * @param {ReturnType<getMergedOptionsForRoot>} options
   */
  function applyPriceUI ($buyCardArea, activeVariantId, options) {
    const $info = $buyCardArea.find('.product-item.product_info').first();
    if (!$info.length) return;
    const map = options.variantPriceLabels || {};
    const $priceLists = $buyCardArea.find('.price_list');
    const $activePriceList = $priceLists.filter('.active').first();
    const ps = $activePriceList.length > 0 && $priceLists.index($activePriceList) > 0 ? '2' : '';
    const defP = String($info.attr('data-product' + ps + '-default-price') || '');
    const defC = String($info.attr('data-product' + ps + '-default-compare-price') || '');
    /* .price_list 在 product-lifestyle-card 里位于 .product_info 之外，需在 buy-card 范围内查找 */
    $buyCardArea.find('.price_list.active .price[data-id]').each(function () {
      const $row = $(this);
      const vid = $row.attr('data-id');
      const st = resolveVariantPriceState(vid, map, defP, defC);
      $row.find('.cur_price').first().text(st.price);
      const $cmp = $row.find('.compare_price').first();
      if ($cmp.length) {
        $cmp.text(st.compare_price);
        if (st.compare_price === '') {
          $cmp.addClass('product-lifestyle-card__compare--hidden');
        } else {
          $cmp.removeClass('product-lifestyle-card__compare--hidden');
        }
      }
    });
    const activeSt = resolveVariantPriceState(activeVariantId, map, defP, defC);
    $info.attr('data-price', activeSt.price);
    $info.attr('data-com-price', activeSt.compare_price);
  }

  function syncPriceFromActiveSwatchInCardRoot ($cardRoot) {
    if (!$cardRoot || !$cardRoot.length) return;
    const opts = getMergedOptionsForRoot($cardRoot, null);
    const $item = $cardRoot.find('.product_item').first();
    const $active = $item.find('.product-color-swatch-wrapper .swatch.active').first();
    if (!$active.length) return;
    const variantId = $active.data('id');
    applyPriceUI($cardRoot.find('.buy-card'), variantId, opts);
  }

  /** @type {WeakMap<Element, Swiper>} */
  const plcSceneSwiperByEl = typeof WeakMap !== 'undefined' ? new WeakMap() : null;

  function destroyLifestyleSceneSwiper (swiperRoot) {
    if (!swiperRoot) return;
    const nested = swiperRoot.querySelector('.swiper');
    const inst =
      swiperRoot.swiper ||
      (nested && nested.swiper) ||
      swiperRoot.__pciPlcSceneSwiper ||
      (plcSceneSwiperByEl && plcSceneSwiperByEl.get(swiperRoot));
    if (inst && typeof inst.destroy === 'function') {
      try {
        inst.destroy(true, true);
      } catch (e) {
        // ignore
      }
    }
    swiperRoot.__pciPlcSceneSwiper = undefined;
    if (plcSceneSwiperByEl) {
      plcSceneSwiperByEl.delete(swiperRoot);
    }
    swiperRoot.dataset.pciPlcSceneInit = '';
  }

  /**
   * @param {HTMLElement} swiperRoot - .product-lifestyle-card__scene-swiper
   */
  function tryInitLifestyleSceneSwiperEl (swiperRoot) {
    if (!swiperRoot || typeof Swiper === 'undefined') return;
    if (!swiperRoot.querySelector('.swiper-slide')) return;
    const slides = swiperRoot.querySelectorAll('.swiper-slide').length;
    if (slides < 2) return;
    const mqlDesktop = isPlcSceneLayoutDesktop(swiperRoot);
    if (!mqlDesktop) return;
    destroyLifestyleSceneSwiper(swiperRoot);
    const prevBtn = swiperRoot.querySelector('.swiper-button-prev');
    const nextBtn = swiperRoot.querySelector('.swiper-button-next');
    const swiper = new Swiper(swiperRoot, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      watchOverflow: true,
      allowTouchMove: true,
      navigation:
        prevBtn && nextBtn
          ? {
            prevEl: prevBtn,
            nextEl: nextBtn,
          }
          : undefined,
    });
    swiperRoot.dataset.pciPlcSceneInit = '1';
    swiperRoot.__pciPlcSceneSwiper = swiper;
    if (plcSceneSwiperByEl) {
      plcSceneSwiperByEl.set(swiperRoot, swiper);
    }
  }

  function initAllLifestyleSceneSwipers (scope) {
    if (typeof Swiper === 'undefined') return;
    const rootEl = scope && scope.nodeType ? scope : document;
    const nodes = rootEl.querySelectorAll
      ? rootEl.querySelectorAll('.product-lifestyle-card__scene-swiper')
      : [];
    nodes.forEach(function (el) {
      tryInitLifestyleSceneSwiperEl(el);
    });
  }

  function refreshLifestyleSceneSwipers (scope) {
    const rootEl = scope && scope.nodeType ? scope : document;
    const nodes = rootEl.querySelectorAll
      ? rootEl.querySelectorAll('.product-lifestyle-card__scene-swiper')
      : [];
    nodes.forEach(function (el) {
      destroyLifestyleSceneSwiper(el);
    });
    initAllLifestyleSceneSwipers(scope);
  }

  let plcResizeScheduled = false;
  function schedulePlcSceneSwiperReflow () {
    if (plcResizeScheduled) return;
    plcResizeScheduled = true;
    window.requestAnimationFrame(function () {
      plcResizeScheduled = false;
      refreshLifestyleSceneSwipers(document);
    });
  }

  function getScope ($root) {
    return $root && $root.length ? $root : $(document);
  }

  /**
   * jQuery `.find()` 不包含 scope 节点自身；PCI 常以单张 `[data-pci-autoload]` 卡为 root，需把根上的 `.product-lifestyle-card` 一并纳入。
   * @param {JQuery | null} $root
   * @returns {JQuery}
   */
  function gatherProductLifestyleCardRoots ($root) {
    const $scope = getScope($root);
    if (!$scope.length) {
      return $();
    }
    return $scope.filter('.product-lifestyle-card').add($scope.find('.product-lifestyle-card'));
  }

  function isSwatchAutoClickBlocked ($swatch, blocklist) {
    const dataIds = new Set((blocklist.dataIds || []).map(String));
    const idAttr = $swatch.attr('data-id');
    if (idAttr != null && idAttr !== '' && dataIds.has(String(idAttr))) {
      return true;
    }
    const dataAttr = $swatch.attr('data') || '';
    for (const fragment of blocklist.dataContains || []) {
      if (dataAttr.includes(fragment)) {
        return true;
      }
    }
    return false;
  }

  function getHiddenVariantIdsSet (options) {
    const arr = options && options.hiddenVariantIds ? options.hiddenVariantIds : [];
    return new Set(arr.map(String));
  }

  function isSwatchConfigHidden ($swatch, hiddenSet) {
    const idAttr = $swatch.attr('data-id');
    if (idAttr == null || idAttr === '') {
      return false;
    }
    return hiddenSet.has(String(idAttr));
  }

  function applyPciConfigHiddenSwatchesInScope ($root) {
    gatherProductLifestyleCardRoots($root).each(function () {
      const $card = $(this);
      const opts = getMergedOptionsForRoot($card, null);
      const hiddenSet = getHiddenVariantIdsSet(opts);
      $card.find('.product-color-swatch-wrapper .swatch').each(function () {
        const $s = $(this);
        if (isSwatchConfigHidden($s, hiddenSet)) {
          $s.addClass('pci-variant--config-hidden');
        } else {
          $s.removeClass('pci-variant--config-hidden');
        }
      });
    });
  }

  function ensureActiveSwatchNotConfigHiddenInScope ($root) {
    gatherProductLifestyleCardRoots($root).each(function () {
      const $card = $(this);
      const pciOpts = getMergedOptionsForRoot($card, null);
      const hiddenSet = getHiddenVariantIdsSet(pciOpts);
      if (!hiddenSet.size) {
        return;
      }
      const $item = $card.find('.product_item').first();
      if (!$item.length) {
        return;
      }
      let $active = $item
        .find('.product_color_item.active .product-color-swatch-wrapper .swatch.active')
        .first();
      if (!$active.length) {
        $active = $item.find('.product-color-swatch-wrapper .swatch.active').first();
      }
      if (!$active.length || !isSwatchConfigHidden($active, hiddenSet)) {
        return;
      }
      $active.removeClass('active');
      triggerFirstAllowedSwatchClick(
        $item.find(
          '.product_color_item.active .product-color-swatch-wrapper .swatch:not(.hidden):not(.pci-variant--config-hidden)'
        ),
        pciOpts.swatchAutoclickBlocklist || { dataContains: [], dataIds: [] },
        hiddenSet
      );
    });
  }

  /**
   * @param {string | undefined | null} title
   * @returns {string[]}
   */
  function splitVariantTitleSegments (title) {
    if (title == null || String(title).trim() === '') {
      return [];
    }
    return String(title)
      .trim()
      .split(/\s*\/\s*/)
      .map(function (part) {
        return String(part).trim();
      });
  }

  /**
   * @param {string | undefined | null} title
   * @param {number} segmentIndex
   * @returns {string}
   */
  function extractSegmentFromVariantTitle (title, segmentIndex) {
    const parts = splitVariantTitleSegments(title);
    if (segmentIndex < 0 || segmentIndex >= parts.length) {
      return '';
    }
    const seg = parts[segmentIndex];
    return seg ? String(seg).trim() : '';
  }

  /**
   * Swatch `data` mirrors variant.title; storefront uses leading segment as Color (e.g. `Black / Standard` or `Black`).
   * @param {string | undefined | null} title
   * @returns {string}
   */
  function extractLeadingOptionFromVariantTitle (title) {
    return extractSegmentFromVariantTitle(title, 0);
  }

  function triggerFirstAllowedSwatchClick ($candidates, blocklist, hiddenSet) {
    const hid =
      hiddenSet && typeof hiddenSet.has === 'function' ? hiddenSet : new Set();
    $candidates.each(function () {
      const $s = $(this);
      if (isSwatchConfigHidden($s, hid)) {
        return;
      }
      if (!isSwatchAutoClickBlocked($s, blocklist)) {
        $s.trigger('click');
        return false;
      }
    });
  }

  /**
   * After non-Color option changes, prefer the same Color swatch (leading segment of variant title) when visible.
   * @param {JQuery} $productItem
   * @param {ReturnType<getMergedOptionsForRoot>} pciOpts
   * @param {string} preferredColorKey
   */
  function triggerPreferredColorSwatchClick ($productItem, pciOpts, preferredColorKey) {
    const blocklist = pciOpts.swatchAutoclickBlocklist || { dataContains: [], dataIds: [] };
    const hiddenSet = getHiddenVariantIdsSet(pciOpts);
    const $candidates = $productItem.find(
      '.product_color_item.active .product-color-swatch-wrapper .swatch:not(.hidden):not(.pci-variant--config-hidden)'
    );
    const key = preferredColorKey ? String(preferredColorKey).trim() : '';
    if (!key) {
      triggerFirstAllowedSwatchClick($candidates, blocklist, hiddenSet);
      return;
    }
    let $matched = null;
    $candidates.each(function () {
      const $s = $(this);
      if (isSwatchConfigHidden($s, hiddenSet)) {
        return;
      }
      if (extractLeadingOptionFromVariantTitle($s.attr('data')) !== key) {
        return;
      }
      if (!isSwatchAutoClickBlocked($s, blocklist)) {
        $matched = $s;
        return false;
      }
    });
    if ($matched && $matched.length) {
      $matched.trigger('click');
      return;
    }
    triggerFirstAllowedSwatchClick($candidates, blocklist, hiddenSet);
  }

  /**
   * Disable `.variant_item[data-pci-option-position]` pills not valid for the active Color (variant.title segments vs Shopify option.position).
   * @param {JQuery} $productItem
   */
  function syncNonColorVariantItemsAvailability ($productItem) {
    if (!$productItem || !$productItem.length) {
      return;
    }
    const $rows = $productItem.find('.variant_item[data-pci-option-position]');
    if (!$rows.length) {
      return;
    }

    let $activeSwatch = $productItem
      .find('.product_color_item.active .product-color-swatch-wrapper .swatch.active')
      .first();
    if (!$activeSwatch.length) {
      $activeSwatch = $productItem.find('.product-color-swatch-wrapper .swatch.active').first();
    }

    const colorKey = extractLeadingOptionFromVariantTitle($activeSwatch.attr('data'));
    if (!colorKey) {
      $rows.find('.variant_values .item').removeClass('disabled');
      return;
    }

    const $cardRoot = $productItem.closest('.product-lifestyle-card');
    const pciOpts = $cardRoot.length ? getMergedOptionsForRoot($cardRoot, null) : mergeOptions(null);
    const hiddenSet = getHiddenVariantIdsSet(pciOpts);
    const $allSwatches = $productItem.find('.product-color-swatch-wrapper .swatch');

    $rows.each(function () {
      const $row = $(this);
      const pos = parseInt(String($row.attr('data-pci-option-position') || '').trim(), 10);
      if (!Number.isFinite(pos) || pos < 1) {
        return;
      }
      const segmentIndex = pos - 1;
      const valid = new Set();
      let contributed = false;

      $allSwatches.each(function () {
        const $sw = $(this);
        if (isSwatchConfigHidden($sw, hiddenSet)) {
          return;
        }
        const raw = $sw.attr('data');
        const parts = splitVariantTitleSegments(raw);
        if (!parts.length || parts[0] !== colorKey) {
          return;
        }
        if (parts.length > segmentIndex) {
          const v = parts[segmentIndex];
          if (v) {
            valid.add(v);
            contributed = true;
          }
        }
      });

      const $items = $row.find('.variant_values .item');
      if (!contributed) {
        $items.removeClass('disabled');
        return;
      }

      $items.each(function () {
        const $it = $(this);
        if ($it.attr('data') === 'custom-item') {
          return;
        }
        const val = String($it.attr('data') || '').trim();
        if (!valid.has(val)) {
          $it.addClass('disabled');
        } else {
          $it.removeClass('disabled');
        }
      });
    });
  }

  /**
   * @param {JQuery | null} $root
   */
  function syncAllNonColorAvailabilityInScope ($root) {
    getScope($root)
      .find('.product_item')
      .each(function () {
        syncNonColorVariantItemsAvailability($(this));
      });
  }

  function activateLifestyleOrGenericVariantVisuals ($productItem, variantId, selectors) {
    const sel = { ...defaultSelectors, ...selectors };
    const vid = String(variantId);
    const $layer = $productItem.find(sel.mobileConfigLayer);
    if ($layer.length) {
      const $match = $layer.find(sel.mobileConfigImg + "[data-id='" + vid + "']");
      if ($match.length) {
        $match.first().addClass('active').siblings(sel.mobileConfigImg).removeClass('active');
      }
    }
    $productItem.find(sel.desktopVariantMedia + " .variant-image[data-id='" + vid + "']").each(function () {
      const $el = $(this);
      $el.addClass('active').siblings('.variant-image').removeClass('active');
    });
    $productItem
      .find(sel.miniProductImgLink + " .variant-image[data-id='" + vid + "']")
      .each(function () {
        const $el = $(this);
        $el.addClass('active').siblings('.variant-image').removeClass('active');
      });
  }

  function setDualSlotMode (productItem, productBox, secondActive) {
    const $pci0 = productItem.find('.product_color_item').eq(0);
    const $pci1 = productItem.find('.product_color_item').eq(1);
    const $title0 = productItem.find('.product-title').eq(0);
    const $title1 = productItem.find('.product-title').eq(1);
    const $disc0 = productItem.find('.discount').eq(0);
    const $disc1 = productItem.find('.discount').eq(1);
    const $img0 = productBox.find('.product-lifestyle-card__variant-media--desktop').eq(0);
    const $img1 = productBox.find('.product-lifestyle-card__variant-media--desktop').eq(1);
    const $price0 = productBox.find('.price_list').eq(0);
    const $price1 = productBox.find('.price_list').eq(1);

    if (secondActive) {
      $pci0.removeClass('active').addClass('hidden');
      $pci1.addClass('active').removeClass('hidden');
      $title0.addClass('hidden');
      $title1.removeClass('hidden');
      $disc0.addClass('hidden');
      $disc1.removeClass('hidden');
      $img0.addClass('hidden');
      $img1.removeClass('hidden');
      $price0.addClass('hidden').removeClass('active');
      $price1.removeClass('hidden').addClass('active');
    } else {
      $pci1.removeClass('active').addClass('hidden');
      $pci0.addClass('active').removeClass('hidden');
      $title1.addClass('hidden');
      $title0.removeClass('hidden');
      $disc1.addClass('hidden');
      $disc0.removeClass('hidden');
      $img1.addClass('hidden');
      $img0.removeClass('hidden');
      $price1.addClass('hidden');
      $price0.removeClass('hidden').addClass('active');
    }
  }

  globalThis.setDualSlotMode = setDualSlotMode;

  function showHideColor ($root) {
    const $scope = getScope($root);
    $scope.find('.product-color-swatch-wrapper .swatch').removeClass('hidden');
    $scope.find('.variant_list .variant_item .item:not(.active)').each(function () {
      const htmlContent = $(this).attr('data');
      $(this).parents('.variant_list').find(".swatch[data*='" + htmlContent + "']").addClass('hidden');
    });
  }

  function applyDefaultSwatchSelections ($root, options) {
    const $scope = getScope($root);
    const blocklist = options.swatchAutoclickBlocklist || { dataContains: [], dataIds: [] };
    const hiddenSet = getHiddenVariantIdsSet(options);
    for (const rawId of options.defaultVariantIds || []) {
      const id = String(rawId);
      if (hiddenSet.has(id)) {
        continue;
      }
      $scope.find(".product-color-swatch-wrapper .swatch[data-id='" + id + "']").each(function () {
        const $swatch = $(this);
        if (!isSwatchAutoClickBlocked($swatch, blocklist)) {
          $swatch.trigger('click');
        }
      });
    }
  }

  /**
   * Cards that use non-Color default value list (newline-separated) on the root element.
   * @param {JQueryStatic | null} $root
   * @returns {JQuery}
   */
  function getCardsWithNonColorDefaults ($root) {
    if ($root && $root.length && $root.is('.product-lifestyle-card')) {
      const raw = $root.attr('data-pci-default-non-color-values');
      if (raw != null && String(raw).trim() !== '') {
        return $root;
      }
      return $();
    }
    return getScope($root).find('.product-lifestyle-card[data-pci-default-non-color-values]');
  }

  const PCI_DEFAULT_NON_COLOR_ITEM_MS = 30;
  const PCI_DEFAULT_NON_COLOR_TAIL_MS = 45;

  /**
   * Programmatic non-Color .item clicks (initial sync). Clears active first so delegated click always runs.
   * After async handler chain, re-applies default variant IDs when set (see applyDefaultSwatchSelections).
   * @param {JQuery} $cards
   * @param {() => void} onAllDone
   */
  function applyDefaultNonColorItemClicks ($cards, onAllDone) {
    if (!$cards || !$cards.length) {
      if (typeof onAllDone === 'function') {
        onAllDone();
      }
      return;
    }
    let pending = $cards.length;
    const finishOne = function () {
      pending -= 1;
      if (pending <= 0 && typeof onAllDone === 'function') {
        onAllDone();
      }
    };

    $cards.each(function () {
      const $card = $(this);
      const cardOpts = getMergedOptionsForRoot($card, null);
      const raw = $card.attr('data-pci-default-non-color-values');
      const values = String(raw || '')
        .split(/\r?\n/)
        .map(function (s) {
          return s.trim();
        })
        .filter(Boolean);

      $card.find('.variant_list .variant_item .item').removeClass('active');

      if (!values.length) {
        if ((cardOpts.defaultVariantIds || []).length > 0) {
          applyDefaultSwatchSelections($card, cardOpts);
        }
        finishOne();
        return;
      }

      values.forEach(function (val, idx) {
        setTimeout(function () {
          const $hit = $card.find('.variant_list .variant_item .item').filter(function () {
            return $(this).attr('data') === val;
          });
          if ($hit.length) {
            $hit.first().trigger('click');
          }
        }, idx * PCI_DEFAULT_NON_COLOR_ITEM_MS);
      });

      const lastIdx = values.length - 1;
      const tailDelay = lastIdx * PCI_DEFAULT_NON_COLOR_ITEM_MS + PCI_DEFAULT_NON_COLOR_TAIL_MS;

      setTimeout(function () {
        if ((cardOpts.defaultVariantIds || []).length > 0) {
          applyDefaultSwatchSelections($card, cardOpts);
        }
        finishOne();
      }, tailDelay);
    });
  }

  function runInitBehavior ($root, options) {
    showHideColor($root);
    applyPciConfigHiddenSwatchesInScope($root);
    ensureActiveSwatchNotConfigHiddenInScope($root);
    applyDefaultSwatchSelections($root, options);
    const scopeEl = $root && $root[0] ? $root[0] : document;

    function finalizePlcSync () {
      if ($root && $root.length && $root.is('.product-lifestyle-card')) {
        syncDiscountFromActiveSwatchInCardRoot($root);
        syncPriceFromActiveSwatchInCardRoot($root);
      } else {
        $(scopeEl).find('.product-lifestyle-card').each(function () {
          const $c = $(this);
          syncDiscountFromActiveSwatchInCardRoot($c);
          syncPriceFromActiveSwatchInCardRoot($c);
        });
      }
      refreshLifestyleSceneSwipers(scopeEl.nodeType ? scopeEl : document);
      applyPciConfigHiddenSwatchesInScope($root);
      ensureActiveSwatchNotConfigHiddenInScope($root);
      syncAllNonColorAvailabilityInScope($root);
    }

    const $ncCards = getCardsWithNonColorDefaults($root);
    if ($ncCards.length) {
      applyDefaultNonColorItemClicks($ncCards, finalizePlcSync);
    } else {
      finalizePlcSync();
    }
  }

  function shouldHandOffToMothersLib ($target) {
    return globalThis.__mothersSale2026DelegatedBound && $target.closest('.mothers-sale-best').length > 0;
  }

  function initProductCardInteractions (root, explicitOptions) {
    const $r = root ? $(root) : null;
    const opts = getMergedOptionsForRoot($r, explicitOptions);
    runInitBehavior($r, opts);
  }

  function bindDelegatedOnce () {
    if (globalThis.__productCardInteractionsDelegatedBound) {
      return;
    }
    globalThis.__productCardInteractionsDelegatedBound = true;

    $(document.body).on('click.pci', '.product-color-swatch-wrapper .swatch', function (e) {
      if (shouldHandOffToMothersLib($(e.target))) {
        return;
      }
      const $swatch = $(this);
      const $productItem = $swatch.parents('.product_item');
      if (!$productItem.length) {
        return;
      }
      const $cardRoot = $swatch.closest('.product-lifestyle-card');
      const options = $cardRoot.length
        ? getMergedOptionsForRoot($cardRoot, null)
        : mergeOptions(null);
      const hiddenSet = getHiddenVariantIdsSet(options);
      if (isSwatchConfigHidden($swatch, hiddenSet)) {
        return;
      }

      $swatch.addClass('active').siblings().removeClass('active');

      const ga4PageName = getPciGa4PageName($cardRoot);
      if (ga4PageName) {
        const colorValue = ($swatch.attr('data') || '').split('/')?.[0]?.trim();
        pushGa4SelectContent('Color', colorValue, ga4PageName);
      }

      const variantId = $swatch.data('id');
      const variantSku = $swatch.data('sku');
      const available = $swatch.data('varavailable');

      $productItem
        .find('.price_list')
        .find('.price[data-id=' + variantId + ']')
        .addClass('active')
        .siblings()
        .removeClass('active');

      if ($productItem.find('.product-lifestyle-card__mobile-primary-config').length) {
        activateLifestyleOrGenericVariantVisuals($productItem, variantId, options.selectors);
      } else {
        $productItem
          .find(".variant-image[data-id='" + variantId + "']")
          .addClass('active')
          .siblings()
          .removeClass('active');
      }

      const $cartBtns = $productItem.find('.addtocart');
      $cartBtns.attr('data-variant-id', variantId);

      if (available) {
        $cartBtns.removeClass('disabled');
        $cartBtns.find('span').first().text('Add to Cart');
      } else {
        $cartBtns.addClass('disabled');
        $cartBtns.find('span').first().text('Sold Out');
      }

      $swatch.parents('.buy-card').find('.product-item.product_info').attr('data-sku', variantSku);

      applyDiscountUI($swatch.parents('.buy-card'), variantId, options);
      applyPriceUI($swatch.parents('.buy-card'), variantId, options);
      syncNonColorVariantItemsAvailability($productItem);
    });

    $(document.body).on('click.pci', '.variant_list .variant_item .item', function (e) {
      if (shouldHandOffToMothersLib($(e.target))) {
        return;
      }
      const $item = $(this);
      if ($item.hasClass('active')) {
        return;
      }
      if ($item.hasClass('disabled')) {
        return;
      }

      $item.addClass('active').siblings().removeClass('active');

      const itemData = $item.attr('data');
      const $productItem = $item.parents('.product_item');
      const $productBox = $item.parents('.product-box');
      const $cardRoot = $item.closest('.product-lifestyle-card');
      const blockOpts = $cardRoot.length
        ? getMergedOptionsForRoot($cardRoot, null)
        : mergeOptions(null);

      const ga4PageNameItem = getPciGa4PageName($cardRoot);
      if (ga4PageNameItem) {
        const $variantItemParent = $item.closest('.variant_item');
        const pciOptionName = $variantItemParent.attr('data-pci-option-name');
        let contentTypeItem = '';
        if (pciOptionName) {
          const lower = pciOptionName.toLowerCase();
          if (lower.indexOf('size') !== -1) {
            contentTypeItem = 'Size';
          } else if (lower.indexOf('charging') !== -1 || lower.indexOf('port') !== -1) {
            contentTypeItem = 'Charging Port';
          }
        } else if (!$variantItemParent.attr('data-pci-option-position')) {
          contentTypeItem = 'Charging Port';
        }
        if (contentTypeItem) {
          const variantValue = $item.text().trim();
          pushGa4SelectContent(contentTypeItem, variantValue, ga4PageNameItem);
        }
      }

      if (itemData === 'custom-item') {
        $productItem.find('.corner-marker').hide();
        $productItem.find('a').attr('href', $item.data('url'));
        $productItem.find('.product-data-div').show();
        setDualSlotMode($productItem, $productBox, true);
        const rootEl = $cardRoot[0];
        const pciSyncKeys = [
          'variant-discount-labels',
          'variant-discount-overrides',
          'variant-prices',
          'variant-price-overrides',
          'default-variant-ids',
          'hidden-variant-ids',
          'swatch-blocklist',
        ];
        pciSyncKeys.forEach(function (k) {
          const suffix = '-2';
          const v = rootEl.getAttribute('data-pci-' + k + suffix);
          if (v != null) {
            rootEl.setAttribute('data-pci-' + k, v);
          } else {
            rootEl.removeAttribute('data-pci-' + k);
          }
        });
        setTimeout(function () {
          const freshOpts = $cardRoot.length
            ? getMergedOptionsForRoot($cardRoot, null)
            : mergeOptions(null);
          const preferredColorKey = extractLeadingOptionFromVariantTitle(
            $productItem.find('.product-color-swatch-wrapper .swatch.active').first().attr('data')
          );
          triggerPreferredColorSwatchClick(
            $productItem,
            freshOpts,
            preferredColorKey
          );
          applyPciConfigHiddenSwatchesInScope($cardRoot);
          ensureActiveSwatchNotConfigHiddenInScope($cardRoot);
          if ($productItem.hasClass('openfit2plus')) {
            $productItem.find('.variant_item').find('.item.active').text('USB-C');
          }
        }, 20);
        return;
      }

      if ($productItem.find('.product_color_item').length > 1) {
        $productItem.find('.corner-marker').show();
        $productItem.find('a').attr('href', $item.parents('.product_info').data('url'));
        $productItem.find('.square').show();
        setDualSlotMode($productItem, $productBox, false);
      }

      if ($productItem.hasClass('openfit2plus')) {
        $productItem.find('.variant_item').find('.item.active').text('USB-C + Wireless Charging');
      }

      if (itemData === 'No Adapter') {
        $productItem.find('.square').hide();
      } else {
        $productItem.find('.square').show();
      }

      if ($productItem.hasClass('openmeet')) {
        const discountText = itemData === 'No Adapter' ? '30% OFF' : '25% OFF';
        $productItem.find('.discount').find('span').text(discountText);
      }

      setTimeout(function () {
        const rootEl = $cardRoot[0];
        if (rootEl) {
          const pciSyncKeys = [
            'variant-discount-labels', 'variant-discount-overrides',
            'variant-prices', 'variant-price-overrides',
            'default-variant-ids', 'hidden-variant-ids', 'swatch-blocklist',
          ];
          pciSyncKeys.forEach(function (k) {
            const v = rootEl.getAttribute('data-pci-' + k + '-1');
            if (v != null) {
              rootEl.setAttribute('data-pci-' + k, v);
            } else {
              rootEl.removeAttribute('data-pci-' + k);
            }
          });
        }
        const freshOpts = $cardRoot.length
          ? getMergedOptionsForRoot($cardRoot, null)
          : mergeOptions(null);
        const preferredColorKey = extractLeadingOptionFromVariantTitle(
          $productItem.find('.product-color-swatch-wrapper .swatch.active').first().attr('data')
        );
        const $scope = $item.closest('.product-lifestyle-card');
        showHideColor($scope.length ? $scope : null);
        triggerPreferredColorSwatchClick(
          $productItem,
          freshOpts,
          preferredColorKey
        );
        syncNonColorVariantItemsAvailability($productItem);
      }, 20);
    });
  }

  bindDelegatedOnce();

  document.addEventListener('DOMContentLoaded', function () {
    $('[data-pci-autoload]').each(function () {
      initProductCardInteractions(this, null);
    });
  });

  document.addEventListener(EVENT_CONTENT_UPDATED, function (e) {
    const root = e && e.detail && e.detail.root;
    const $root = root ? $(root) : null;
    const scopeEl = root && root.nodeType ? root : document;
    if ($root && $root.length) {
      const opts = getMergedOptionsForRoot($root, null);
      runInitBehavior($root, opts);
    } else {
      const o = mergeOptions(null);
      runInitBehavior(null, o);
    }
    refreshLifestyleSceneSwipers(scopeEl);
  });

  window.addEventListener('resize', function () {
    schedulePlcSceneSwiperReflow();
  });

  globalThis.initProductCardInteractions = initProductCardInteractions;
})();
